/**
 * RuleResultsList — Display per-rule execution results with drill-down.
 *
 * Used by RulesManagementView after /execute-batch or /execute-direct.
 * Shows: rule label, status badge, total/passing/failing counts, pass rate,
 * execution time, expandable sample failures.
 */

import { Fragment, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  XCircle,
  FileText,
  Sparkles,
} from "lucide-react";
import type { RuleResult, ExecutionStatus } from "../../api/rules";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RuleResultsListProps {
  results: RuleResult[];
  /** Map rule_id → rule label (for display). Falls back to rule_id if missing. */
  ruleLabels?: Record<string, string>;
  /** Optional callback when user clicks the "Export to Backlog" button on a row. */
  onExportToBacklog?: (result: RuleResult) => void;
  /** Hide the Backlog button entirely (Day 4 feature — hidden during Day 3 demo). */
  exportToBacklogEnabled?: boolean;
  /** Shown as a pill above the list (e.g., "postgres://host:5434/fsa_demo"). */
  sourceDescription?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusStyle(status: ExecutionStatus): {
  // @ts-expect-error mypy cleanup — runtime contract is correct, type model needs follow-up
  icon: JSX.Element;
  color: string;
  bg: string;
  label: string;
} {
  switch (status) {
    case "pass":
      return {
        icon: <CheckCircle2 className="w-4 h-4" />,
        color: "text-emerald-400",
        bg: "bg-emerald-400/10 border-emerald-400/30",
        label: "Pass",
      };
    case "fail":
      return {
        icon: <XCircle className="w-4 h-4" />,
        color: "text-red-400",
        bg: "bg-red-400/10 border-red-400/30",
        label: "Fail",
      };
    case "error":
      return {
        icon: <AlertTriangle className="w-4 h-4" />,
        color: "text-amber-400",
        bg: "bg-amber-400/10 border-amber-400/30",
        label: "Error",
      };
    case "skipped":
    default:
      return {
        icon: <FileText className="w-4 h-4" />,
        color: "text-white/40",
        bg: "bg-white/5 border-white/10",
        label: "Skipped",
      };
  }
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatPercent(p: number): string {
  return `${(p * 100).toFixed(2)}%`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RuleResultsList({
  results,
  ruleLabels = {},
  onExportToBacklog,
  exportToBacklogEnabled = false,
  sourceDescription,
}: RuleResultsListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (results.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-8 text-center">
        <Sparkles className="w-8 h-8 text-white/30 mx-auto mb-3" />
        <div className="text-white/60 text-sm">
          No rule execution results yet. Click <strong>Run All Rules</strong> to
          execute your rules against the connected data source.
        </div>
      </div>
    );
  }

  // Aggregate summary
  const failingTotal = results.filter((r) => r.status === "fail").length;
  const errorTotal = results.filter((r) => r.status === "error").length;
  const passingTotal = results.filter((r) => r.status === "pass").length;
  const totalFailingRows = results.reduce(
    (sum, r) => sum + (r.failing_rows || 0),
    0,
  );

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="text-white/80">
                <strong>{passingTotal}</strong> pass
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <XCircle className="w-4 h-4 text-red-400" />
              <span className="text-white/80">
                <strong>{failingTotal}</strong> fail
              </span>
            </div>
            {errorTotal > 0 && (
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="text-white/80">
                  <strong>{errorTotal}</strong> error
                </span>
              </div>
            )}
            <div className="text-white/40">·</div>
            <div className="text-white/80">
              <strong>{formatNumber(totalFailingRows)}</strong> total failing
              rows surfaced
            </div>
          </div>
          {sourceDescription && (
            <div className="flex items-center gap-1.5 text-xs text-white/50">
              <Database className="w-3.5 h-3.5" />
              <span className="font-mono">{sourceDescription}</span>
            </div>
          )}
        </div>
      </div>

      {/* Per-rule rows */}
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/50">
            <tr>
              <th className="px-4 py-3 text-left w-8"></th>
              <th className="px-4 py-3 text-left">Rule</th>
              <th className="px-4 py-3 text-center w-24">Status</th>
              <th className="px-4 py-3 text-right w-28">Rows</th>
              <th className="px-4 py-3 text-right w-32">Failing</th>
              <th className="px-4 py-3 text-right w-24">Pass Rate</th>
              <th className="px-4 py-3 text-right w-20">Time</th>
              {exportToBacklogEnabled && (
                <th className="px-4 py-3 text-right w-32">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {results.map((r) => {
              const style = statusStyle(r.status);
              const isExpanded = expandedId === r.id;
              const label =
                ruleLabels[r.rule_id] || `Rule ${r.rule_id.substring(0, 8)}`;
              const hasDetails =
                r.sample_failures.length > 0 || r.error_message;

              return (
                <Fragment key={r.id}>
                  <tr
                    className="hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() =>
                      hasDetails && setExpandedId(isExpanded ? null : r.id)
                    }
                  >
                    <td className="px-4 py-3 text-white/40">
                      {hasDetails ? (
                        isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )
                      ) : null}
                    </td>
                    <td
                      className="px-4 py-3 text-white/90 truncate max-w-md"
                      title={label}
                    >
                      {label}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${style.bg} ${style.color}`}
                      >
                        {style.icon}
                        {style.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-white/60 font-mono">
                      {formatNumber(r.total_rows_evaluated)}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-mono font-bold ${
                        r.failing_rows > 0
                          ? "text-red-400"
                          : "text-emerald-400/70"
                      }`}
                    >
                      {formatNumber(r.failing_rows)}
                    </td>
                    <td className="px-4 py-3 text-right text-white/70 font-mono">
                      {formatPercent(r.pass_rate)}
                    </td>
                    <td className="px-4 py-3 text-right text-white/40 font-mono">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {r.execution_time_ms}ms
                      </span>
                    </td>
                    {exportToBacklogEnabled && (
                      <td className="px-4 py-3 text-right">
                        {r.status === "fail" && onExportToBacklog && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onExportToBacklog(r);
                            }}
                            className="text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-300 border border-blue-500/30 hover:bg-blue-500/20 transition-colors"
                          >
                            → Backlog
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {isExpanded && hasDetails && (
                    <tr key={`${r.id}-details`} className="bg-black/20">
                      <td
                        colSpan={exportToBacklogEnabled ? 8 : 7}
                        className="px-4 py-4"
                      >
                        {r.error_message ? (
                          <div className="space-y-2">
                            <div className="text-xs font-semibold text-amber-400 uppercase tracking-wider">
                              Execution Error
                            </div>
                            <pre className="text-xs bg-black/40 border border-amber-400/20 p-3 rounded-md whitespace-pre-wrap overflow-x-auto text-amber-100/80 font-mono">
                              {r.error_message}
                            </pre>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                                Sample failures ({r.sample_failures.length}{" "}
                                shown)
                              </div>
                              <div className="text-xs text-white/40">
                                Rows as returned by the rule, from the source
                              </div>
                            </div>
                            <div className="max-h-60 overflow-y-auto custom-scrollbar">
                              <table className="w-full text-xs">
                                <thead className="sticky top-0 bg-black/40 text-white/50 uppercase">
                                  <tr>
                                    <th className="px-2 py-1 text-left">
                                      Row ID
                                    </th>
                                    <th className="px-2 py-1 text-left">
                                      Column
                                    </th>
                                    <th className="px-2 py-1 text-left">
                                      Expected
                                    </th>
                                    <th className="px-2 py-1 text-left">
                                      Category
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="font-mono">
                                  {r.sample_failures
                                    .slice(0, 50)
                                    .map((s, idx) => (
                                      <tr
                                        key={idx}
                                        className="border-t border-white/5"
                                      >
                                        <td className="px-2 py-1 text-white/70">
                                          {s.row_identifier ??
                                            s.row_index ??
                                            "—"}
                                        </td>
                                        <td className="px-2 py-1 text-white/70">
                                          {s.column_name || "—"}
                                        </td>
                                        <td className="px-2 py-1 text-white/60">
                                          {s.expected || "—"}
                                        </td>
                                        <td className="px-2 py-1 text-white/50">
                                          {s.actual_category ||
                                            s.violation_type}
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
